/**
 * Normalize value to string
 * @param {*} value - Value to normalize
 * @returns {string} - Normalized string or empty string if null/undefined
 */
const normalizeString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

/**
 * Check if code is running in a browser context.
 * @returns {boolean}
 */
const canUseWindow = () => typeof window !== 'undefined';

/**
 * Safely get (and initialize) window.dataLayer.
 * @returns {Array|null}
 */
const getDataLayer = () => {
  if (!canUseWindow()) {
    return null;
  }

  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/**
 * Push payload into dataLayer with light validation.
 * @param {Object} payload
 * @returns {boolean}
 */
const pushToDataLayer = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const dataLayer = getDataLayer();
  if (!dataLayer) {
    return false;
  }

  dataLayer.push(payload);
  return true;
};

/**
 * Get base dataLayer from window object
 * @returns {Object} - Base dataLayer object or empty object
 */
const getBaseLayer = () => (canUseWindow() && window.AZDS_DATA_LAYER && typeof window.AZDS_DATA_LAYER === 'object'
  ? window.AZDS_DATA_LAYER
  : {});

/**
 * Parse birthday string to Date object
 * Supports formats: MM/DD/YYYY and YYYY-MM-DD (ISO)
 * @param {string} birthday - Birthday string
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
const parseBirthday = (birthday) => {
  if (!birthday) {
    return null;
  }

  const normalized = birthday.replace(/\s+/g, '');
  const parts = normalized.split('/');
  if (parts.length !== 3) {
    const isoParts = birthday.split('-');
    if (isoParts.length !== 3) {
      return null;
    }
    const [year, month, day] = isoParts.map((part) => parseInt(part, 10));
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  const [month, day, year] = parts.map((part) => parseInt(part, 10));
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
};

/**
 * Calculate age group from birthday string
 * @param {string} birthday - Birthday string (MM/DD/YYYY or YYYY-MM-DD)
 * @returns {string} - Age group string ('<18', '18-25', '26-35', '36-45', '46-55', '56-65', '66+')
 */
const getAgeGroupFromBirthday = (birthday) => {
  const date = parseBirthday(birthday);
  if (!date) {
    return '';
  }

  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  const monthDiff = now.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
    age -= 1;
  }

  if (age < 18) {
    return '<18';
  }
  if (age <= 25) {
    return '18-25';
  }
  if (age <= 35) {
    return '26-35';
  }
  if (age <= 45) {
    return '36-45';
  }
  if (age <= 55) {
    return '46-55';
  }
  if (age <= 65) {
    return '56-65';
  }
  return '66+';
};

/**
 * Build user dataLayer object from profile data
 * @param {Object} profile - User profile object
 * @param {string} profile.id - User ID
 * @param {string} profile.sf_account_id - Salesforce account ID (determines userType)
 * @param {string} profile.zip_code - ZIP code
 * @param {string} profile.country - Country code
 * @param {string} profile.birthday - Birthday string
 * @returns {Object} - User dataLayer object with normalized properties
 */
export const buildUserDataLayerFromProfile = (profile = {}) => {
  const base = getBaseLayer();

  const userId = normalizeString(profile.id || base.userId);
  const userType = profile.sf_account_id ? 'Account' : 'Lead';
  const zip = normalizeString(profile.zip_code || base.zip);
  const country = normalizeString(profile.country || base.country);
  const age = getAgeGroupFromBirthday(profile.birthday) || normalizeString(base.age);

  return {
    userId,
    userType,
    pageLanguage: normalizeString(base.pageLanguage),
    zip,
    country,
    age,
    userLoyaltyType: normalizeString(base.userLoyaltyType),
    environment: normalizeString(base.environment),
  };
};

/**
 * Push user data to dataLayer and update base layer
 * @param {Object} data - User data object to push
 */
export const pushUserDataLayer = (data) => {
  if (!data || typeof data !== 'object') {
    return;
  }

  if (pushToDataLayer(data) && canUseWindow()) {
    window.AZDS_DATA_LAYER = { ...getBaseLayer(), ...data };
  }
};

/**
 * Build and push user dataLayer from profile data
 * @param {Object} profile - User profile object
 */
export const pushUserDataLayerFromProfile = (profile) => {
  const payload = buildUserDataLayerFromProfile(profile);
  pushUserDataLayer(payload);
};

/**
 * Push generic event to dataLayer
 * @param {Object} eventData - Event data object
 * @param {string} eventData.event - Event name (required)
 * @param {string} eventData.section - Event section
 * @param {string} eventData.label - Event label
 */
export const pushDataLayerEvent = ({ event, section, label }) => {
  if (!event) {
    return;
  }

  const payload = {
    event,
    eventTimestamp: Date.now(),
    section: normalizeString(section),
    label: normalizeString(label),
  };

  pushToDataLayer(payload);
};

/**
 * Field label mappings for profile form
 */
const PROFILE_FIELD_LABELS = {
  first_name: 'firstName',
  last_name: 'lastName',
  salutation: 'salutation',
  country: 'country',
  zip_code: 'zip code',
};

/**
 * Field label mappings for preferences form (abbreviated)
 */
const PREFERENCES_FIELD_LABELS = {
  birthday: 'bday',
  language: 'lang',
  nationality: 'nat',
  phone_number: 'ph1',
  mobile_phone: 'ph2',
  preferred_contact_method: 'pref',
  interested_in: 'int',
  favorite_collection: 'fav1',
  travel_inspirations: 'insp',
  wedding: 'wed',
  anniversary: 'Ann',
  other: 'Oth',
  other_date: 'Oth',
  allergies_dietary_restrictions: 'allerg',
  craft: 'craft',
};

/**
 * Push profile update event to dataLayer
 * @param {Array<string>} fields - Array of updated field names
 * @param {boolean} success - Whether the update was successful
 * @param {string} errorMessage - Error message if update failed
 * @param {string} section - Section name ('My Profile' or 'Profile Preferences')
 */
export const pushProfileUpdateEvent = ({
  fields = [],
  success = true,
  errorMessage = '',
  section = 'My Profile',
}) => {
  const fieldLabels = section === 'Profile Preferences'
    ? PREFERENCES_FIELD_LABELS
    : PROFILE_FIELD_LABELS;

  const formattedFields = fields
    .map((field) => fieldLabels[field] || field)
    .join('|');

  const payload = {
    event: 'update',
    eventTimestamp: Date.now(),
    fields: formattedFields,
    section,
    label: success ? 'Success' : 'Failure',
    errorMessage: normalizeString(errorMessage),
  };

  pushToDataLayer(payload);
};

/**
 * Get filled fields from preferences form data
 * @param {Object} formData - Preferences form data
 * @returns {Array<string>} - Array of field names that have values
 */
const getFilledPreferencesFields = (formData) => {
  const filledFields = [];
  Object.keys(PREFERENCES_FIELD_LABELS).forEach((field) => {
    const value = formData[field];
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      filledFields.push(field);
    }
  });
  return filledFields;
};

/**
 * Push preferences page view event to dataLayer
 * @param {Object} formData - Current preferences form data
 */
export const pushPreferencesPageViewEvent = (formData = {}) => {
  const filledFields = getFilledPreferencesFields(formData);
  const formattedFields = filledFields
    .map((field) => PREFERENCES_FIELD_LABELS[field] || field)
    .join('|');

  const payload = {
    event: 'page_view',
    eventTimestamp: Date.now(),
    fields: formattedFields,
    section: 'Profile Preferences',
    label: 'Preferences Fields Status',
  };

  pushToDataLayer(payload);
};

/**
 * Push certificate registration page view event to dataLayer
 * @param {Object} data - Certificate registration data object
 * @param {string} data.name - Decanter name
 * @param {string} data.id - Decanter ID/SKU
 * @param {string} data.collectionName - Collection name
 * @param {string} data.section - Section where CTA was clicked
 * @param {string} data.method - Method (e.g., 'NFC', 'QR')
 */
export const pushCertificateRegistrationPageViewEvent = (data = {}) => {
  const payload = {
    event: 'page_view',
    eventTimestamp: Date.now(),
    name: data.name,
    id: data.id,
    collectionName: data.collectionName,
    section: 'Certificate Registration',
    label: 'UnAuthenticated Registration Start',
    method: data.method,
  };

  pushToDataLayer(payload);
};

/**
 * Push CTA click event to dataLayer
 * @param {Object} ctaData - CTA data object
 * @param {string} ctaData.name - Item name (for decanter clicks)
 * @param {string} ctaData.id - Item ID/SKU (for decanter clicks)
 * @param {string} ctaData.collectionName - Collection name
 * @param {string} ctaData.section - Section where CTA was clicked
 * @param {string} ctaData.label - CTA label or status
 */
export const pushCtaEvent = ({
  name = '',
  id = '',
  collectionName = '',
  section = '',
  context = '',
  label = '',
  method = '',
}) => {
  const normalizedSection = normalizeString(section);
  const normalizedContext = normalizeString(context) || normalizedSection;
  const normalizedLabel = normalizeString(label).replace(/`/g, '"');
  const payload = {
    event: 'cta',
    eventTimestamp: Date.now(),
    section: normalizedSection,
    context: normalizedContext,
    label: normalizedLabel,
  };

  // Add optional fields only if they have values
  if (name) payload.name = name;
  if (id) payload.id = id;
  if (collectionName) payload.collection_name = collectionName;
  if (method) payload.method = method;

  pushToDataLayer(payload);
};

/**
 * Push "images" interaction (per Destination Detail Page spec).
 * @param {Object} data
 * @param {string} data.section
 * @param {string} data.context
 * @param {string} data.label
 * @param {string} data.name
 * @param {string} data.id
 * @param {string} data.collectionName
 */
export const pushImagesEvent = ({
  name = '',
  id = '',
  collectionName = '',
  section = '',
  context = '',
  label = '',
}) => {
  const payload = {
    event: 'images',
    eventTimestamp: Date.now(),
  };

  if (name) payload.name = name;
  if (id) payload.id = id;
  if (collectionName) payload.collection_name = collectionName;
  if (section) payload.section = section;
  if (context) payload.context = context;
  if (label) payload.label = label;

  pushToDataLayer(payload);
};

/**
 * Push "video_play" when a video begins playback (spec: on play).
 * @param {Object} data
 * @param {string} data.section
 * @param {string} data.context
 * @param {string} data.label
 * @param {string} data.name
 * @param {string} data.id
 * @param {string} data.collectionName
 */
export const pushVideoPlayEvent = ({
  name = '',
  id = '',
  collectionName = '',
  section = '',
  context = '',
  label = '',
}) => {
  const payload = {
    event: 'video_play',
    eventTimestamp: Date.now(),
  };

  if (name) payload.name = name;
  if (id) payload.id = id;
  if (collectionName) payload.collection_name = collectionName;
  if (section) payload.section = section;
  if (context) payload.context = context;
  if (label) payload.label = label;

  pushToDataLayer(payload);
};

/**
 * Push funnel event to dataLayer
 * @param {Object} funnelData - Funnel data object
 * @param {string} funnelData.section - Funnel section (e.g., 'Sign In', 'Sign Up')
 * @param {number} funnelData.step - Funnel step number
 * @param {string} funnelData.label - Funnel label/status
 * @param {string} funnelData.method - Method (e.g., 'Email', 'Google')
 * @param {string} funnelData.errorMessage - Error message (optional)
 */
export const pushFunnelEvent = ({
  section = '',
  step = 1,
  label = '',
  method = '',
  errorMessage = '',
  name = '',
  id = '',
  collectionName = '',
}) => {
  const payload = {
    event: 'funnel',
    eventTimestamp: Date.now(),
    section,
    step,
    label,
    method,
  };

  if (name) payload.name = name;
  if (id) payload.id = id;
  if (collectionName) payload.collection_name = collectionName;

  // Add errorMessage only if it has a value
  if (errorMessage) {
    payload.errorMessage = errorMessage;
  }

  pushToDataLayer(payload);
};

/**
 * Push top menu click event to dataLayer
 * @param {Object} menuData - Menu data object
 * @param {string} menuData.section - Current section/page
 * @param {string} menuData.label - Menu item label
 */
export const pushTopMenuEvent = ({
  section = '',
  label = '',
}) => {
  const payload = {
    event: 'top_menu',
    eventTimestamp: Date.now(),
    section,
    label,
  };

  pushToDataLayer(payload);
};

/**
 * Push inner menu click event to dataLayer
 * @param {Object} menuData - Menu data object
 * @param {string} menuData.section - Current section/page
 * @param {string} menuData.label - Menu item label
 */
export const pushInnerMenuEvent = ({
  section = '',
  label = '',
}) => {
  const payload = {
    event: 'inner_menu',
    eventTimestamp: Date.now(),
    section,
    label,
  };

  pushToDataLayer(payload);
};

/**
 * Push footer click event to dataLayer
 * @param {Object} footerData - Footer data object
 * @param {string} footerData.section - Current section/page
 * @param {string} footerData.label - Footer item label
 */
export const pushFooterEvent = ({
  section = '',
  label = '',
}) => {
  const payload = {
    event: 'footer',
    eventTimestamp: Date.now(),
    section,
    label,
  };

  pushToDataLayer(payload);
};

/**
 * Push error event to dataLayer
 * @param {Object} errorData - Error data object
 * @param {string} errorData.section - Current section/page
 * @param {string} errorData.label - Error item label
 * @param {string} errorData.errorMessage - Error message
 * @param {string} errorData.method - Method (e.g., 'Email', 'Google')
 * @param {string} errorData.name - Item name (for decanter clicks)
 * @param {string} errorData.id - Item ID/SKU (for decanter clicks)
 * @param {string} errorData.collectionName - Collection name
 */
export const pushErrorEvent = ({
  section = '',
  label = '',
  errorMessage = '',
  method = '',
  name = '',
  id = '',
  collectionName = '',
}) => {
  const payload = {
    event: 'error',
    eventTimestamp: Date.now(),
    section,
    label,
  };

  if (errorMessage) payload.errorMessage = errorMessage;
  if (method) payload.method = method;
  if (name) payload.name = name;
  if (id) payload.id = id;
  if (collectionName) payload.collection_name = collectionName;

  pushToDataLayer(payload);
};

/**
 * Push article click event to dataLayer
 * @param {Object} articleData - Article data object
 * @param {string} articleData.section - Article section
 * @param {string} articleData.label - Article label
 */
export const pushArticleEvent = ({
  section = '',
  label = '',
}) => {
  const payload = {
    event: 'article',
    eventTimestamp: Date.now(),
    section,
    label,
  };

  pushToDataLayer(payload);
};

/**
 * Push email subscription event to dataLayer
 */
export const pushEmailSubscriptionEvent = () => {
  const payload = {
    event: 'email_subscription',
    eventTimestamp: Date.now(),
  };

  pushToDataLayer(payload);
};

/**
 * Dashboard | Contact Us — successful submit (per analytics PDF).
 * Maps camelCase args to GA4 payload keys field_1..field_3 from the spec.
 * @param {Object} data
 * @param {string} data.country Country selected (field_1)
 * @param {string} data.experienceInterest Experience interest (field_2)
 * @param {string} data.subject Subject selected (field_3)
 */
export const pushContactUsEvent = ({
  country = '',
  experienceInterest = '',
  subject = '',
}) => {
  const payload = {
    event: 'contact_us',
    eventTimestamp: Date.now(),
    field_1: normalizeString(country),
    field_2: normalizeString(experienceInterest),
    field_3: normalizeString(subject),
  };

  pushToDataLayer(payload);
};

/**
 * Dashboard / Contact Us — failed submit.
 * @param {Object} data
 * @param {string} data.country Country selected (field_1)
 * @param {string} data.experienceInterest Experience interest (field_2)
 * @param {string} data.subject Subject selected (field_3)
 * @param {string} data.errorMessage Error message returned by CF7
 */
export const pushContactUsErrorEvent = ({
  country = '',
  experienceInterest = '',
  subject = '',
  errorMessage = '',
}) => {
  const payload = {
    event: 'contact_us_error',
    eventTimestamp: Date.now(),
    field_1: normalizeString(country),
    field_2: normalizeString(experienceInterest),
    field_3: normalizeString(subject),
    error_message: normalizeString(errorMessage),
  };

  pushToDataLayer(payload);
};

/**
 * GA4 recommended pattern: clear previous ecommerce object before a new ecommerce event.
 */
export const pushEcommerceReset = () => {
  pushToDataLayer({ ecommerce: null });
};

/**
 * Push a GA4 ecommerce-style event (view_item, add_to_cart, begin_checkout,
 * add_payment_info, purchase). Matches dashboard PDFs: reset ecommerce,
 * then push event + ecommerce payload.
 * @param {string} eventName
 * @param {Object|null} ecommerce
 */
export const pushGa4EcommerceEvent = (eventName, ecommerce = null) => {
  if (!eventName) {
    return;
  }

  pushEcommerceReset();

  const payload = {
    event: eventName,
    eventTimestamp: Date.now(),
  };

  if (ecommerce && typeof ecommerce === 'object') {
    payload.ecommerce = ecommerce;
  }

  pushToDataLayer(payload);
};

/**
 * @param {Object|null} ecommerce
 */
export const pushViewItemEvent = (ecommerce) => {
  pushGa4EcommerceEvent('view_item', ecommerce);
};

/**
 * @param {Object|null} ecommerce
 */
export const pushAddToCartEvent = (ecommerce) => {
  pushGa4EcommerceEvent('add_to_cart', ecommerce);
};

/**
 * @param {Object|null} ecommerce
 */
export const pushBeginCheckoutEvent = (ecommerce) => {
  pushGa4EcommerceEvent('begin_checkout', ecommerce);
};

/**
 * @param {Object|null} ecommerce
 */
export const pushAddPaymentInfoEvent = (ecommerce) => {
  pushGa4EcommerceEvent('add_payment_info', ecommerce);
};

/**
 * @param {Object|null} ecommerce
 */
export const pushPurchaseEvent = (ecommerce) => {
  pushGa4EcommerceEvent('purchase', ecommerce);
};

/**
 * FAQ accordion open (Destination Detail PDF): section, label, optional decanter fields.
 * @param {Object} data
 * @param {string} data.section
 * @param {string} data.label
 * @param {string} data.name
 * @param {string} data.id
 * @param {string} data.collectionName
 */
export const pushFaqEvent = ({
  section = '',
  label = '',
  name = '',
  id = '',
  collectionName = '',
}) => {
  const payload = {
    event: 'faq',
    eventTimestamp: Date.now(),
    section: normalizeString(section),
    label: normalizeString(label),
  };

  if (name) payload.name = name;
  if (id) payload.id = id;
  if (collectionName) payload.collection_name = collectionName;

  pushToDataLayer(payload);
};
